import React, { useState } from 'react';
import {
    CreditCard,
    ChevronLeft,
    CheckCircle2,
    AlertCircle,
    RefreshCw,
    Lock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { useKYC } from '@/shared/hooks/useKYC';
import { cn } from '@/shared/lib/utils';

const CreditCardVerificationPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { submitApplication } = useKYC(user?.uid);

    const [cardNumber, setCardNumber] = useState('');
    const [cardHolderName, setCardHolderName] = useState('');
    const [expiryMonth, setExpiryMonth] = useState('');
    const [expiryYear, setExpiryYear] = useState('');
    const [cvc, setCvc] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const formatCardNumber = (value: string) => {
        // 숫자만 추출
        const numbers = value.replace(/\D/g, '');
        // 4자리씩 공백으로 구분
        const formatted = numbers.match(/.{1,4}/g)?.join(' ') || numbers;
        return formatted.substring(0, 19); // 16자리 + 3개 공백
    };

    const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatCardNumber(e.target.value);
        setCardNumber(formatted);
    };

    const handleSubmit = async () => {
        // 유효성 검증
        const cardNumberOnly = cardNumber.replace(/\s/g, '');
        if (cardNumberOnly.length !== 16) {
            alert('카드 번호 16자리를 모두 입력해주세요.');
            return;
        }
        if (!cardHolderName.trim()) {
            alert('카드 소유자 이름을 입력해주세요.');
            return;
        }
        if (!expiryMonth || !expiryYear) {
            alert('유효기간을 입력해주세요.');
            return;
        }
        if (cvc.length < 3) {
            alert('CVC 번호를 입력해주세요.');
            return;
        }

        // 월 유효성 검증
        const month = parseInt(expiryMonth);
        if (month < 1 || month > 12) {
            alert('올바른 유효기간(월)을 입력해주세요.');
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await submitApplication('CREDIT_CARD', {
                cardNumber: cardNumber,           // 전체 카드번호
                cardHolderName: cardHolderName.trim(),
                expiryMonth,
                expiryYear,
                cvc,
            });

            if (result.success) {
                alert('신용카드 등록이 완료되었습니다.\n관리자 확인 후 고급 인증이 승인됩니다.');
                navigate('/verification');
            } else {
                alert('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
            }
        } catch (error) {
            console.error('Credit card submission error:', error);
            alert('신청 중 오류가 발생했습니다.');
        }
        setIsSubmitting(false);
    };

    return (
        <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-700">
            <button
                onClick={() => navigate('/verification')}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm font-bold group"
            >
                <ChevronLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" /> 뒤로가기
            </button>

            <div className="space-y-2">
                <h1 className="text-3xl font-black text-foreground flex items-center gap-3 uppercase tracking-tight">
                    <CreditCard className="h-8 w-8 text-blue-600 dark:text-blue-500" /> 신용카드 등록
                </h1>
                <p className="text-muted-foreground font-bold text-sm">
                    신용카드 정보를 등록하여 고급 인증을 완료하세요.
                </p>
            </div>

            <div className="rounded-[2.5rem] bg-card border border-border p-8 shadow-2xl space-y-8">
                {/* 보안 안내 */}
                <div className="p-6 rounded-3xl bg-blue-500/5 border border-blue-500/10 flex items-start gap-4">
                    <Lock className="h-5 w-5 text-blue-600 dark:text-blue-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                        <p className="text-xs font-black text-blue-600 dark:text-blue-500 uppercase tracking-widest">보안 안내</p>
                        <p className="text-[10px] font-bold text-muted-foreground leading-relaxed">
                            • 입력하신 정보는 암호화되어 안전하게 보관됩니다.<br />
                            • 카드 번호는 마지막 4자리만 저장되며, CVC는 저장되지 않습니다.<br />
                            • 본 인증은 신원확인 목적이며, 결제가 발생하지 않습니다.
                        </p>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* 카드 번호 */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest px-1">
                            카드 번호
                        </label>
                        <input
                            type="text"
                            placeholder="1234 5678 9012 3456"
                            value={cardNumber}
                            onChange={handleCardNumberChange}
                            maxLength={19}
                            className="w-full h-14 bg-background border border-border rounded-2xl px-6 text-sm font-bold text-foreground outline-none focus:border-blue-500 transition-all font-mono"
                        />
                    </div>

                    {/* 카드 소유자 */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest px-1">
                            카드 소유자 이름
                        </label>
                        <input
                            type="text"
                            placeholder="홍길동"
                            value={cardHolderName}
                            onChange={(e) => setCardHolderName(e.target.value)}
                            className="w-full h-14 bg-background border border-border rounded-2xl px-6 text-sm font-bold text-foreground outline-none focus:border-blue-500 transition-all"
                        />
                    </div>

                    {/* 유효기간 & CVC */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest px-1">
                                유효기간 (월/연도)
                            </label>
                            <div className="flex gap-2 items-center">
                                <input
                                    type="text"
                                    placeholder="MM"
                                    value={expiryMonth}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '');
                                        if (val.length <= 2) setExpiryMonth(val);
                                    }}
                                    maxLength={2}
                                    className="w-20 h-14 bg-background border border-border rounded-2xl px-3 text-sm font-bold text-foreground outline-none focus:border-blue-500 transition-all text-center font-mono"
                                />
                                <span className="text-2xl font-bold text-muted-foreground">/</span>
                                <input
                                    type="text"
                                    placeholder="YY"
                                    value={expiryYear}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '');
                                        if (val.length <= 2) setExpiryYear(val);
                                    }}
                                    maxLength={2}
                                    className="w-20 h-14 bg-background border border-border rounded-2xl px-3 text-sm font-bold text-foreground outline-none focus:border-blue-500 transition-all text-center font-mono"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest px-1">
                                CVC
                            </label>
                            <input
                                type="password"
                                placeholder="123"
                                value={cvc}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    if (val.length <= 4) setCvc(val);
                                }}
                                maxLength={4}
                                className="w-full h-14 bg-background border border-border rounded-2xl px-6 text-sm font-bold text-foreground outline-none focus:border-blue-500 transition-all text-center font-mono"
                            />
                        </div>
                    </div>
                </div>

                {/* 혜택 안내 */}
                <div className="p-6 rounded-3xl bg-emerald-500/5 border border-emerald-500/10 flex items-start gap-4">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                        <p className="text-xs font-black text-emerald-600 dark:text-emerald-500 uppercase tracking-widest">고급 인증 혜택</p>
                        <p className="text-[10px] font-bold text-muted-foreground leading-relaxed">
                            고급 인증 완료 시 출금 기능이 활성화되며, 일일 거래 한도가 상향 조정됩니다.
                        </p>
                    </div>
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !cardNumber || !cardHolderName || !expiryMonth || !expiryYear || !cvc}
                    className={cn(
                        "w-full h-16 rounded-2xl font-black shadow-xl transition-all uppercase tracking-widest text-sm flex items-center justify-center gap-3",
                        "bg-blue-600 text-white shadow-blue-600/20 hover:bg-blue-500",
                        "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                    )}
                >
                    {isSubmitting ? <RefreshCw className="h-5 w-5 animate-spin" /> : "신용카드 등록 신청하기"}
                </button>
            </div>
        </div>
    );
};

export default CreditCardVerificationPage;
